import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Table,
  Flex,
  Typography,
  Button,
  Tag,
  Radio,
  Input,
  Checkbox,
  Form,
  Spin,
  Alert,
  Space,
  Divider,
} from 'antd';
import {
  DollarOutlined,
  CreditCardOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  CardElement,
  Elements,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_API_KEY } from '../../../config';
import { useQuery } from '@apollo/client';
import type { ColumnsType } from 'antd/es/table';
import { formatCurrency, formatDateMMDDYYYY } from '../../../utils/helpers';
import { loftColors, loftFontWeights } from '../../../antd/theme';
import {
  HistoryTableRow,
  GroupedHistory,
  getEffectiveStatus,
  getFinancialsHistoryStatusStyles,
  getInvoiceDisplayIdsForAnalyticsFromInvoiceRows,
} from './Financials.utils';
import { usePayInvoice, BillingAddress } from './usePayInvoice';
import {
  PaymentMethodsQuery,
  PaymentMethodsQueryResult,
} from '../../../schema/generated/schema';

const { Text } = Typography;

/** Nested Elements so Pay Invoices has its own card element; Account also mounts AddCardModal (one card per Elements instance). */
const payNowStripePromise = loadStripe(STRIPE_API_KEY);

/** Pay Invoices flow is USD-only; matches Stripe and BAS charge amounts. */
const PAY_NOW_DIALOG_CURRENCY = 'USD';

const CARD_ELEMENT_OPTIONS = {
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '14px',
      color: '#092C47',
      fontFamily: 'inherit',
      '::placeholder': { color: '#8495A3' },
    },
    invalid: {
      color: '#E63817',
    },
  },
};

interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: string;
  expYear: string;
  name: string | null;
}

interface PayNowDialogProps {
  open: boolean;
  invoiceRows: HistoryTableRow[];
  billingAccountId: string;
  onClose: () => void;
  onPaymentSuccess: () => void;
}

const PayNowDialogInner: React.FC<PayNowDialogProps> = ({
  open,
  invoiceRows,
  billingAccountId,
  onClose,
  onPaymentSuccess,
}) => {
  const [paymentType, setPaymentType] = useState<'saved_card' | 'new_card'>(
    'saved_card'
  );
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [saveCreditCard, setSaveCreditCard] = useState(false);
  const [billingAddress, setBillingAddress] = useState<BillingAddress>({
    name: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'United States',
    countryCode: 'US',
    phoneNumber: '',
  });

  const stripe = useStripe();
  const elements = useElements();
  const { submitPayment, isProcessing, error, clearError } = usePayInvoice();

  const {
    data: paymentMethodsData,
    loading: loadingCards,
    error: paymentMethodsError,
  } = useQuery<PaymentMethodsQueryResult>(PaymentMethodsQuery);

  const savedCards: SavedCard[] = useMemo(() => {
    if (!paymentMethodsData?.paymentMethods) return [];
    return paymentMethodsData.paymentMethods
      .filter(
        (pm): pm is NonNullable<typeof pm> =>
          pm != null && pm.__typename === 'StripeCard'
      )
      .map((card) => ({
        id: card.id,
        brand: 'brand' in card ? (card.brand as string) : '',
        last4: 'last4' in card ? (card.last4 as string) : '',
        expMonth: 'expMonth' in card ? (card.expMonth as string) : '',
        expYear: 'expYear' in card ? (card.expYear as string) : '',
        name: 'name' in card ? (card.name as string | null) : null,
      }));
  }, [paymentMethodsData]);

  useEffect(() => {
    if (loadingCards) return;
    if (paymentMethodsError) {
      if (selectedCardId) setSelectedCardId('');
      return;
    }
    if (savedCards.length === 0) {
      if (selectedCardId) setSelectedCardId('');
      return;
    }
    const stillValid = savedCards.some((c) => c.id === selectedCardId);
    if (!stillValid) {
      setSelectedCardId(savedCards[0].id);
    }
  }, [savedCards, selectedCardId, loadingCards, paymentMethodsError]);

  const totalAmountDue = useMemo(
    () =>
      invoiceRows.reduce((sum, row) => {
        if (row.kind !== 'invoice') return sum;
        return sum + (row.data as GroupedHistory).remainingBalance;
      }, 0),
    [invoiceRows]
  );

  /** Invoice rows from the history table may include `children` (applied payments); Ant Design Table treats that as tree data and shows expand controls. Pay dialog is flat-only. */
  const payDialogTableRows = useMemo(
    () =>
      invoiceRows.map((row) => {
        if (row.kind !== 'invoice') return row;
        return { kind: row.kind, data: row.data, key: row.key };
      }),
    [invoiceRows]
  );

  const totalAmountInCents = Math.round(totalAmountDue * 100);

  const handleBillingChange = useCallback(
    (field: keyof BillingAddress, value: string) => {
      setBillingAddress((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handlePay = useCallback(async () => {
    if (window.analytics) {
      window.analytics.track('Pay Invoice Submitted', {
        object_id: 'pay-invoice-button',
        object_name: 'Pay Invoice',
        object_location: 'Pay Now Dialog',
        order_id: getInvoiceDisplayIdsForAnalyticsFromInvoiceRows(invoiceRows),
      });
    }

    clearError();

    const invoiceRecordNos = invoiceRows
      .filter((r) => r.kind === 'invoice')
      .map((r) => (r.data as GroupedHistory).invoice.recordNo);

    const success = await submitPayment({
      invoiceRecordNos,
      totalAmountInCents,
      billingAccountId,
      paymentType,
      stripeCardId: paymentType === 'saved_card' ? selectedCardId : undefined,
      saveCreditCard: paymentType === 'new_card' ? saveCreditCard : undefined,
      billingAddress: paymentType === 'new_card' ? billingAddress : undefined,
    });

    if (success) {
      onPaymentSuccess();
    }
  }, [
    clearError,
    invoiceRows,
    submitPayment,
    totalAmountInCents,
    billingAccountId,
    paymentType,
    selectedCardId,
    saveCreditCard,
    billingAddress,
    onPaymentSuccess,
  ]);

  const handleClose = useCallback(() => {
    if (isProcessing) return;
    clearError();
    onClose();
  }, [isProcessing, clearError, onClose]);

  const isPayDisabled =
    isProcessing ||
    invoiceRows.length === 0 ||
    (paymentType === 'saved_card' && !selectedCardId) ||
    (paymentType === 'new_card' &&
      (!billingAddress.name.trim() ||
        !billingAddress.street1.trim() ||
        !billingAddress.city.trim() ||
        !billingAddress.state.trim() ||
        !billingAddress.zipcode.trim() ||
        !billingAddress.country.trim() ||
        !billingAddress.countryCode.trim()));

  const columns: ColumnsType<HistoryTableRow> = [
    {
      title: 'Record #',
      key: 'recordNo',
      width: '20%',
      render: (_, row) => {
        if (row.kind !== 'invoice') return null;
        return (
          <Text strong style={{ fontSize: 12 }}>
            {row.data.invoice.docNo}
          </Text>
        );
      },
    },
    {
      title: 'Date',
      key: 'date',
      width: '16%',
      render: (_, row) => {
        if (row.kind !== 'invoice') return null;
        return (
          <Text style={{ fontSize: 12 }}>
            {formatDateMMDDYYYY(row.data.invoice.whenCreated)}
          </Text>
        );
      },
    },
    {
      title: 'Due Date',
      key: 'dueDate',
      width: '16%',
      render: (_, row) => {
        if (row.kind !== 'invoice') return null;
        return (
          <Text style={{ fontSize: 12 }}>
            {formatDateMMDDYYYY(row.data.invoice.whenDue)}
          </Text>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: '14%',
      render: (_, row) => {
        if (row.kind !== 'invoice') return null;
        const styles = getFinancialsHistoryStatusStyles(row);
        return (
          <Tag
            style={{
              backgroundColor: styles.backgroundColor,
              borderColor: styles.borderColor,
              color: styles.textColor,
              fontWeight: loftFontWeights.semibold,
              borderRadius: 999,
              padding: '2px 12px',
            }}
          >
            {getEffectiveStatus(row.data.invoice)}
          </Tag>
        );
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: '16%',
      render: (_, row) => {
        if (row.kind !== 'invoice') return null;
        return (
          <Text style={{ fontSize: 12 }}>
            {formatCurrency(
              row.data.invoice.totalEntered,
              PAY_NOW_DIALOG_CURRENCY
            )}
          </Text>
        );
      },
    },
    {
      title: 'Balance Due',
      key: 'balance',
      width: '18%',
      render: (_, row) => {
        if (row.kind !== 'invoice') return null;
        const balance = (row.data as GroupedHistory).remainingBalance;
        return (
          <Text strong style={{ fontSize: 12 }}>
            {formatCurrency(balance.toFixed(2), PAY_NOW_DIALOG_CURRENCY)}
          </Text>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <Flex justify="space-between" align="center">
          <Text strong style={{ fontSize: 18 }}>
            Pay Invoices
          </Text>
          <Flex gap={8} align="baseline" style={{ marginRight: 24 }}>
            <Text style={{ fontSize: 12, color: loftColors.grays[75] }}>
              {invoiceRows.length} invoice{invoiceRows.length !== 1 ? 's' : ''}
            </Text>
            <Text strong style={{ fontSize: 16 }}>
              Total:{' '}
              {formatCurrency(
                totalAmountDue.toFixed(2),
                PAY_NOW_DIALOG_CURRENCY
              )}
            </Text>
          </Flex>
        </Flex>
      }
      open={open}
      onCancel={handleClose}
      width={900}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={handlePay}
            disabled={isPayDisabled}
            loading={isProcessing}
          >
            {isProcessing
              ? 'Processing...'
              : `Pay ${formatCurrency(
                  totalAmountDue.toFixed(2),
                  PAY_NOW_DIALOG_CURRENCY
                )}`}
          </Button>
        </Flex>
      }
      destroyOnClose
      closable={!isProcessing}
      maskClosable={!isProcessing}
    >
      <Spin spinning={isProcessing} tip="Processing payment...">
        <Table<HistoryTableRow>
          columns={columns}
          dataSource={payDialogTableRows}
          rowKey="key"
          pagination={false}
          size="middle"
          tableLayout="fixed"
          scroll={{ y: 200 }}
          expandable={{ showExpandColumn: false }}
        />

        <Divider />

        <Text
          strong
          style={{ fontSize: 14, display: 'block', marginBottom: 12 }}
        >
          Payment Method
        </Text>

        <Radio.Group
          value={paymentType}
          onChange={(e) => {
            setPaymentType(e.target.value);
            clearError();
          }}
          style={{ marginBottom: 16, display: 'block' }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Radio value="saved_card">
              <CreditCardOutlined style={{ marginRight: 4 }} />
              Use a saved payment method
            </Radio>
            <Radio value="new_card">
              <PlusOutlined style={{ marginRight: 4 }} />
              Use a new card
            </Radio>
          </Space>
        </Radio.Group>

        {paymentType === 'saved_card' && (
          <div style={{ marginLeft: 24, marginBottom: 16 }}>
            {loadingCards ? (
              <Spin size="small" />
            ) : paymentMethodsError ? (
              <Alert
                type="error"
                showIcon
                message="Unable to load saved payment methods"
                description={paymentMethodsError.message}
                style={{ fontSize: 12 }}
              />
            ) : savedCards.length === 0 ? (
              <Text style={{ color: loftColors.grays[75], fontSize: 12 }}>
                No saved payment methods found.{' '}
                <Button
                  type="link"
                  onClick={() => setPaymentType('new_card')}
                  style={{
                    padding: 0,
                    height: 'auto',
                    fontSize: 12,
                    verticalAlign: 'baseline',
                    color: loftColors.grays[75],
                  }}
                >
                  Add a new card
                </Button>
              </Text>
            ) : (
              <Radio.Group
                value={selectedCardId}
                onChange={(e) => setSelectedCardId(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  {savedCards.map((card) => (
                    <Radio key={card.id} value={card.id}>
                      <Text style={{ fontSize: 13 }}>
                        {card.brand} ending in {card.last4} &nbsp;
                        <Text
                          style={{
                            fontSize: 11,
                            color: loftColors.grays[50],
                          }}
                        >
                          (expires {card.expMonth}/{card.expYear})
                        </Text>
                      </Text>
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            )}
          </div>
        )}

        {paymentType === 'new_card' && (
          <div style={{ marginLeft: 24, marginBottom: 16 }}>
            {!stripe || !elements ? (
              <Text
                style={{
                  color: loftColors.grays[75],
                  fontSize: 13,
                  display: 'block',
                  marginBottom: 8,
                }}
              >
                Loading payment form…
              </Text>
            ) : (
              <Form layout="vertical" size="small">
                <Form.Item
                  label="Card Details"
                  style={{ marginBottom: 12 }}
                  required
                >
                  <div
                    style={{
                      border: `1px solid ${loftColors.grays[25]}`,
                      borderRadius: 4,
                      padding: '8px 12px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                </Form.Item>

                <Form.Item
                  label="Name on Card"
                  style={{ marginBottom: 12 }}
                  required
                >
                  <Input
                    value={billingAddress.name}
                    onChange={(e) =>
                      handleBillingChange('name', e.target.value)
                    }
                    placeholder="Full name"
                  />
                </Form.Item>

                <Flex gap={12}>
                  <Form.Item
                    label="Street Address"
                    style={{ flex: 2, marginBottom: 12 }}
                    required
                  >
                    <Input
                      value={billingAddress.street1}
                      onChange={(e) =>
                        handleBillingChange('street1', e.target.value)
                      }
                      placeholder="Street address"
                    />
                  </Form.Item>
                  <Form.Item
                    label="Apt / Suite"
                    style={{ flex: 1, marginBottom: 12 }}
                  >
                    <Input
                      value={billingAddress.street2}
                      onChange={(e) =>
                        handleBillingChange('street2', e.target.value)
                      }
                      placeholder="Apt, suite, etc."
                    />
                  </Form.Item>
                </Flex>

                <Flex gap={12}>
                  <Form.Item
                    label="City"
                    style={{ flex: 1, marginBottom: 12 }}
                    required
                  >
                    <Input
                      value={billingAddress.city}
                      onChange={(e) =>
                        handleBillingChange('city', e.target.value)
                      }
                      placeholder="City"
                    />
                  </Form.Item>
                  <Form.Item
                    label="State"
                    style={{ flex: 1, marginBottom: 12 }}
                    required
                  >
                    <Input
                      value={billingAddress.state}
                      onChange={(e) =>
                        handleBillingChange('state', e.target.value)
                      }
                      placeholder="State"
                    />
                  </Form.Item>
                  <Form.Item
                    label="ZIP Code"
                    style={{ flex: 1, marginBottom: 12 }}
                    required
                  >
                    <Input
                      value={billingAddress.zipcode}
                      onChange={(e) =>
                        handleBillingChange('zipcode', e.target.value)
                      }
                      placeholder="ZIP code"
                    />
                  </Form.Item>
                </Flex>

                <Checkbox
                  checked={saveCreditCard}
                  onChange={(e) => setSaveCreditCard(e.target.checked)}
                  style={{ marginTop: 4, marginBottom: 8 }}
                >
                  <Text style={{ fontSize: 12 }}>
                    Save this card for future payments
                  </Text>
                </Checkbox>
              </Form>
            )}
          </div>
        )}

        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            closable
            onClose={clearError}
            style={{ marginTop: 8 }}
          />
        )}
      </Spin>
    </Modal>
  );
};

const PayNowDialog: React.FC<PayNowDialogProps> = (props) => (
  <Elements stripe={payNowStripePromise}>
    <PayNowDialogInner {...props} />
  </Elements>
);

export default PayNowDialog;
